// Iter 386 — Asientos de reclasificación 5124 → gasto para bills PAGADAS (Alegra no permite editarlas por API).
// Flujo: Proponer (lee Alegra, no modifica) → Aprobar (contador) → Contabilizar (POST /journals) → Anular si hace falta.
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { AsientoDetalleDialog } from './AsientoDetalleDialog';
import { BookOpenCheck, Eye, Loader2, Search, ShieldCheck, Send, Undo2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const fmtCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
const ESTADO_CLS = {
  propuesto: 'text-slate-700 border-slate-300', aprobado: 'text-blue-700 border-blue-300',
  contabilizando: 'text-blue-700 border-blue-300', contabilizado: 'text-emerald-700 border-emerald-300',
  error: 'text-red-700 border-red-300', anulado: 'text-gray-500 border-gray-300',
};

export function AsientosReclasificacionCard() {
  const [estado, setEstado] = useState({ status: 'idle' });
  const [asientos, setAsientos] = useState([]);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [detalleId, setDetalleId] = useState(null);
  const [ocupado, setOcupado] = useState(null);
  const pollRef = useRef(null);
  const cerrarDetalle = useCallback(() => setDetalleId(null), []);

  const loadEstado = useCallback(async () => {
    const r = await api.get('/contabilidad/cuentas-alegra/asientos/estado');
    setEstado(r.data || { status: 'idle' });
    return r.data;
  }, []);
  const loadAsientos = useCallback(async () => {
    const r = await api.get('/contabilidad/cuentas-alegra/asientos');
    setAsientos(r.data?.asientos || []);
  }, []);

  useEffect(() => { loadEstado().catch(() => {}); loadAsientos().catch(() => {}); }, [loadEstado, loadAsientos]);

  useEffect(() => {
    if (estado.status === 'proponiendo' && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const est = await loadEstado();
          if (est.status !== 'proponiendo') { clearInterval(pollRef.current); pollRef.current = null; loadAsientos(); }
        } catch { /* siguiente tick */ }
      }, 2500);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [estado.status, loadEstado, loadAsientos]);

  const accion = async (a, fn, okMsg) => {
    setOcupado(a.id);
    try {
      const r = await fn();
      toast.success(typeof okMsg === 'function' ? okMsg(r.data) : okMsg);
      await loadAsientos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error en la operación');
      await loadAsientos();
    } finally {
      setOcupado(null);
    }
  };

  const handleProponer = async () => {
    try {
      const r = await api.post('/contabilidad/cuentas-alegra/asientos/proponer', { anio });
      if (r.data?.ok) { toast.info(`Proponiendo asientos ${anio} — lee cada bill en Alegra, no modifica nada`); loadEstado(); }
      else toast.error(r.data?.error || 'No se pudo iniciar');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error iniciando propuesta');
    }
  };
  const handleFecha = (a, fecha) => fecha && fecha !== a.fecha && accion(a, () => api.put(`/contabilidad/cuentas-alegra/asientos/${a.id}`, { fecha }), `Fecha del asiento ${a.periodo} actualizada`);
  const handleAprobar = (a) => {
    const comentario = window.prompt(`Aprobación del asiento ${a.periodo} (${a.n_bills} facturas · ${fmtCLP(a.total_neto)}).\n\nIndica quién aprueba (contador) y un comentario:`);
    if (comentario === null) return;
    accion(a, () => api.post(`/contabilidad/cuentas-alegra/asientos/${a.id}/aprobar`, { aprobar: true, comentario }), `Asiento ${a.periodo} aprobado — ya puedes contabilizarlo en Alegra`);
  };
  const handleDesaprobar = (a) => accion(a, () => api.post(`/contabilidad/cuentas-alegra/asientos/${a.id}/aprobar`, { aprobar: false }), `Aprobación del asiento ${a.periodo} revertida`);
  const handleContabilizar = (a) => {
    if (!window.confirm(`Se creará en Alegra un comprobante contable con fecha ${fmtFecha(a.fecha)}:\nDebe cuentas de gasto / Haber 5124 por ${fmtCLP(a.total_neto)} (${a.n_bills} facturas pagadas de ${a.periodo}).\n\n¿Contabilizar?`)) return;
    accion(a, () => api.post(`/contabilidad/cuentas-alegra/asientos/${a.id}/contabilizar`), (d) => `Comprobante Alegra #${d.alegra_journal_number || d.alegra_journal_id} creado (${d.n_bills} facturas · ${fmtCLP(d.total_neto)})`);
  };
  const handleAnular = (a) => {
    const motivo = window.prompt(`Anular el comprobante Alegra #${a.alegra_journal_number || a.alegra_journal_id} (${a.periodo}). Se ELIMINA en Alegra y las facturas vuelven al informe.\n\nMotivo:`);
    if (motivo === null) return;
    accion(a, () => api.post(`/contabilidad/cuentas-alegra/asientos/${a.id}/anular`, { motivo }), `Asiento ${a.periodo} anulado`);
  };
  const handleDescartar = (a) => window.confirm(`¿Descartar la propuesta ${a.periodo}? Podrás volver a proponerla.`) && accion(a, () => api.delete(`/contabilidad/cuentas-alegra/asientos/${a.id}`), `Propuesta ${a.periodo} descartada`);

  const proponiendo = estado.status === 'proponiendo';
  const totalProp = asientos.filter((a) => ['propuesto', 'aprobado'].includes(a.estado)).reduce((s, a) => s + (a.total_neto || 0), 0);

  return (
    <Card data-testid="card-asientos-reclasificacion">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BookOpenCheck className="w-4 h-4 text-indigo-600" /> Asientos de reclasificación — bills pagadas en 5124
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Alegra no permite editar facturas de proveedor con pago aplicado. Para corregir el P&L por cuenta se genera <strong>un comprobante contable por mes</strong>:
          Debe cuenta de gasto (por OT) / Haber 5124 "Facturas por Recibir", con el neto de las líneas que siguen en 5124. Nada se envía a Alegra sin <strong>aprobación del contador</strong>.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-[10px] text-muted-foreground">Año</label>
            <Input type="number" value={anio} onChange={(e) => setAnio(Number(e.target.value))} className="w-28 text-sm" data-testid="input-anio-asientos" />
          </div>
          <Button onClick={handleProponer} disabled={proponiendo} variant="outline" data-testid="btn-proponer-asientos">
            {proponiendo ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />} Proponer asientos (lee Alegra)
          </Button>
          {totalProp > 0 && <span className="text-xs text-muted-foreground" data-testid="asientos-total-pendiente">Pendiente de contabilizar: <strong>{fmtCLP(totalProp)}</strong></span>}
        </div>

        {proponiendo && (
          <div className="text-xs bg-blue-50 border border-blue-200 rounded p-3" data-testid="asientos-progreso">
            <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" /> {estado.fase} {estado.total ? `(${estado.progreso || 0}/${estado.total})` : ''}
          </div>
        )}
        {estado.status === 'error' && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="asientos-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {estado.error}
          </div>
        )}
        {estado.status === 'listo' && estado.resumen && (
          <div className="text-xs text-muted-foreground" data-testid="asientos-resumen">
            Última propuesta {estado.anio}: {estado.resumen.bills_leidas} bills leídas en Alegra · {estado.resumen.bills_en_asientos} en {estado.resumen.periodos} asiento(s) mensual(es).
          </div>
        )}

        {asientos.length > 0 && (
          <div className="border rounded overflow-x-auto" data-testid="tabla-asientos">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-2 py-1.5">Período</th>
                  <th className="text-left px-2 py-1.5">Fecha asiento</th>
                  <th className="text-right px-2 py-1.5">Facturas</th>
                  <th className="text-right px-2 py-1.5">Neto 5124 → gasto</th>
                  <th className="text-center px-2 py-1.5">Estado</th>
                  <th className="text-left px-2 py-1.5">Aprobación / Alegra</th>
                  <th className="text-right px-2 py-1.5">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {asientos.map((a) => {
                  const busy = ocupado === a.id;
                  return (
                    <tr key={a.id} className="border-t" data-testid={`asiento-${a.periodo}-${a.estado}`}>
                      <td className="px-2 py-1 font-mono">{a.periodo}</td>
                      <td className="px-2 py-1">
                        {['propuesto', 'error'].includes(a.estado)
                          ? <Input type="date" defaultValue={a.fecha} onBlur={(e) => handleFecha(a, e.target.value)} className="h-7 w-36 text-xs" disabled={busy} data-testid={`input-fecha-${a.periodo}`} />
                          : fmtFecha(a.fecha)}
                      </td>
                      <td className="px-2 py-1 text-right font-mono">{a.n_bills}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtCLP(a.total_neto)}</td>
                      <td className="px-2 py-1 text-center"><Badge variant="outline" className={`text-[10px] ${ESTADO_CLS[a.estado] || ''}`}>{a.estado}</Badge></td>
                      <td className="px-2 py-1 text-muted-foreground max-w-[280px]">
                        {a.comentario_aprobacion && <div className="truncate" title={a.comentario_aprobacion}>✓ {a.comentario_aprobacion}</div>}
                        {a.alegra_journal_id && <div>Comprobante Alegra #{a.alegra_journal_number || a.alegra_journal_id} · {fmtFecha(a.contabilizado_at)}</div>}
                        {a.estado === 'anulado' && <div className="truncate" title={a.motivo_anulacion}>Anulado: {a.motivo_anulacion}</div>}
                        {a.error && <div className="text-red-700 truncate" title={a.error}>{a.error}</div>}
                      </td>
                      <td className="px-2 py-1">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setDetalleId(a.id)} data-testid={`btn-ver-${a.periodo}`}><Eye className="w-3.5 h-3.5 mr-1" /> Ver</Button>
                          {['propuesto', 'error'].includes(a.estado) && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy} onClick={() => handleAprobar(a)} data-testid={`btn-aprobar-${a.periodo}`}><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Aprobar</Button>
                          )}
                          {a.estado === 'aprobado' && (
                            <>
                              <Button size="sm" className="h-7 px-2 text-xs bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={() => handleContabilizar(a)} data-testid={`btn-contabilizar-${a.periodo}`}>
                                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Contabilizar en Alegra
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busy} onClick={() => handleDesaprobar(a)} title="Quitar aprobación" data-testid={`btn-desaprobar-${a.periodo}`}><Undo2 className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                          {a.estado === 'contabilizado' && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-700" disabled={busy} onClick={() => handleAnular(a)} data-testid={`btn-anular-${a.periodo}`}><Undo2 className="w-3.5 h-3.5 mr-1" /> Anular</Button>
                          )}
                          {['propuesto', 'error', 'anulado'].includes(a.estado) && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground" disabled={busy} onClick={() => handleDescartar(a)} title="Descartar propuesta" data-testid={`btn-descartar-${a.periodo}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {asientos.length === 0 && !proponiendo && (
          <p className="text-xs text-muted-foreground italic" data-testid="asientos-vacio">Sin asientos propuestos. Ejecuta "Proponer asientos" para armar la propuesta mensual a partir del informe de arriba.</p>
        )}
      </CardContent>
      <AsientoDetalleDialog asientoId={detalleId} onClose={cerrarDetalle} />
    </Card>
  );
}
